import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { facilityPickQueryOptions } from "../../../features/facility-pick/api/facilities";
import { contentLinesQueryOptions } from "../../../features/message-content/api/message-content";
import { MessageContent } from "../../../features/message-content/components/message-content";

const TYPE_WORDS = {
  ALERT: "Alert",
  DAILY_SUMMARY: "Daily summary",
  WEEKLY_SUMMARY: "Weekly summary",
};

/**
 * `RPM-092`'s message-content lines (`J-296`), with each line's History (`RPM-063`).
 *
 * THE ROUTE COMPOSES FEATURES, as `.claude/rules/coding.md` asks: the buildings are `facility-pick`'s
 * list, the lines are `message-content`'s and the History tab is `change-history`'s. The tab is
 * rendered only for an account holding `audit: ["read"]`; the server decides on every request.
 *
 * WHO MAY CHANGE WHICH LIST. Both are `routing: ["update"]`; the tenant's default lines are asked it
 * at tenant scope by the server, a building's own at that building (`content-lines.routes.ts`). The
 * screen shows a withheld change in words; the server refuses it again whatever the screen showed.
 */
function MessageContentScreen() {
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canReadHistory = usePermission({ audit: ["read"] });
  // The defaults are the tenant's: only a tenant-wide grant changes them. A building's own lines are
  // changed tenant-wide or at that building (`D-1389`).
  const canChangeDefaults = usePermission({ routing: ["update"] }, ["TENANT"]);
  const canChangeBuildings = usePermission({ routing: ["update"] }, [
    "TENANT",
    "CERTIFIED_FACILITY",
  ]);
  const facilities = useQuery(facilityPickQueryOptions());
  const lines = useQuery({ ...contentLinesQueryOptions(), enabled: canReadHistory });
  const buildings = (facilities.data?.items ?? []).map(({ facilityId, name }) => ({
    facilityId,
    name,
  }));
  const nameOf = (facilityId: string | null) =>
    facilityId === null
      ? "Tenant default"
      : (buildings.find((b) => b.facilityId === facilityId)?.name ?? facilityId);
  return (
    <div className="flex flex-col gap-4 pt-4">
      <HistoryTabs
        showHistory={canReadHistory}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
        }
        detailsLabel="Content lines"
        details={
          <MessageContent
            facilities={buildings}
            canChangeDefaults={canChangeDefaults}
            canChangeBuildings={canChangeBuildings}
          />
        }
        history={
          <RecordHistory
            entity="notification-attributes"
            records={(lines.data ?? []).map((l) => ({
              id: l.notificationAttributeId,
              label: `${nameOf(l.facilityId)}, ${TYPE_WORDS[l.notificationType]} ${String(l.ordinal)}: ${l.attributeText}`,
            }))}
            noun="Content line"
            caption={(label) => `Change history of content line ${label}`}
            columnLabels={{ attribute_text: "Line text", ordinal: "Position", is_active: "Active" }}
          />
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/message-content")({
  validateSearch: historyTabSearchSchema,
  component: MessageContentScreen,
  staticData: { screenName: "Message content" },
});
